import React, { Component } from 'react';
import { connect } from 'react-redux';
import AudioRecorder from 'components/AudioRecAndPlay';
import PropTypes from 'prop-types';
import InactivityManager from 'utils/InactivityManager';
import { sendRecorderSkippedEventData } from 'store/actions/activity';
import audioPlayer from 'icons/audioplayer.png';
import SVGCharacters from 'icons/Components/SVGCharacters';
import styles from './VoiceRecorder.module.scss';
import { playMultipleSounds } from 'utils/play-multiple-sounds';
import { showVisualCue, hideVisualCue } from 'store/actions/ui';
import { t } from '../translations';

export class VoiceRecorder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recorder: false,
      showRecordButton: false,
      done: false,
      round: 0
    };

    this.didUnmount = false;
    this.recordings = [];
    this.inactivityManager = new InactivityManager();
    this.numberOfRounds = props.currentRound.data.recordings;
    this.timers = [];
  }

  componentDidUpdate({ isVideoModalOpen: wasVideoOpen }) {
    const { isVideoModalOpen: isVideoOpen } = this.props;
    const { recorder } = this.state;

    // video is being open
    if (!wasVideoOpen && isVideoOpen) {
      this.setState({ recorder: false });
      this.props.currentRound.stopAllSounds();
      this.promise && this.promise.cancel();
    }

    // video is being closed
    if (wasVideoOpen && !isVideoOpen) {
      if (!this.instructionsPlayed) {
        this.playInitialInstructions();
      } else if (!recorder) {
        this.setState({ recorder: true, showRecordButton: true });
      }
      document.getElementById('tutorial-button').focus();
    }
  }

  addVisualCue(message, interval = 1000) {
    this.props.showVisualCue(message);
    this.timers.push(setTimeout(() => this.props.hideVisualCue(), interval));
  }

  playInitialInstructions() {
    const {
      currentRound: {
        audio,
        data: { instructions }
      }
    } = this.props;

    this.instructionsPlayed = true;
    this.promise = playMultipleSounds(500, audio, instructions.introduction, instructions.number)
      .then(() => playMultipleSounds(1000, audio, instructions.beforeRecording))
      .then(() => this.setState({ recorder: true, showRecordButton: true }));
  }

  async componentDidMount() {
    const { micPermissions, onRoundEnd, isVideoModalOpen } = this.props;

    if (!micPermissions) {
      this.props.sendRecorderSkippedEventData();
      onRoundEnd();
      return;
    }

    // we'll delay instructions until we close modal
    if (isVideoModalOpen) return;
    this.playInitialInstructions();
  }

  componentWillUnmount() {
    this.didUnmount = true;
    this.props.currentRound.stopAllSounds();
    this.promise && this.promise.cancel();
    this.timers.length && this.timers.forEach(tID => clearTimeout(tID));
  }

  addRecording = recording => {
    if (this.didUnmount) return;
    const { round } = this.state;
    if (!recording) this.logError(recording);
    this.recordings.push(recording || []);
    this.setState({ recorder: false, round: round + 1, done: round + 1 === this.numberOfRounds });
    this.handleRoundEnd();
  };

  handleRoundEnd = async () => {
    const {
      props: {
        currentRound: {
          audio,
          data: { instructions, affirmations }
        }
      },
      state: { done, round }
    } = this;
    this.setState({ showRecordButton: false });
    this.promise =
      round === 1
        ? playMultipleSounds(0, audio, affirmations[0], instructions.beforeRerecording)
        : playMultipleSounds(0, audio, affirmations[1]);
    await this.promise;
    done && this.sendData();
    !done && this.setState({ recorder: true, showRecordButton: true });
  };

  hideRecorder() {
    this.setState({ recorder: false });
  }

  logError = () => {
    const {
      props: {
        currentRound: { logError: roundLogError }
      }
    } = this;
    const error = {
      error: 'NoRecording',
      competency: 'Speech',
      expected: {
        filename: `recording_${Date.now()}.mp3`
      },
      actual: {
        filename: undefined
      },
      timestamp: new Date().toISOString()
    };
    roundLogError?.(error);
  };

  recordingEnd = () => {
    const {
      currentRound: {
        audio,
        data: {
          instructions: { afterRecording, afterRerecording }
        }
      }
    } = this.props;

    const roundIdx = 2 - this.recordings.length;
    this.addVisualCue('success');

    return roundIdx === 1
      ? playMultipleSounds(500, audio, afterRecording)
      : playMultipleSounds(500, audio, afterRerecording);
  };

  replayInstructions = () => {
    const {
      currentRound: {
        audio,
        data: { instructions }
      }
    } = this.props;
    playMultipleSounds(0, audio, instructions.repeat);
  };

  sendData() {
    this.props.saveRecordings(this.recordings);
    this.props.onRoundEnd();
  }

  render() {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            length,
            number,
            word,
            image,
            instructions: { afterRecording, beforeRecording }
          }
        },
        micPermissions
      },
      state: { done, showRecordButton, recorder, round }
    } = this;

    const NumberSVG = SVGCharacters[number];

    if (false === micPermissions) {
      return <div>{t(`MicPermissionsRequired`)}</div>;
    }

    const playPressTheRedButton = audio[beforeRecording[1].src];
    return (
      <div className={styles.SoundRecorder}>
        <div className={styles.Image}>
          {NumberSVG && <NumberSVG />}
          {word && <h3>{word}</h3>}
          {image && <img src={image.src} alt={image.description} id="voice-recorder-image" />}
        </div>
        <div className={styles.Instructions} id="voice-recorder-instructions">
          {!done && (
            <img
              src={audioPlayer}
              onClick={this.replayInstructions}
              onKeyDown={ev => {
                ev.key === 'Enter' && this.replayInstructions();
              }}
              tabIndex="0"
              role="button"
              aria-label={t('lblInstructions')}
              alt=""
            />
          )}
        </div>
        <div className={styles.Recorder}>
          {recorder && !done && (
            <AudioRecorder
              beforePlayback={afterRecording}
              showRecordButton={showRecordButton}
              maxRecordingLength={length}
              onRoundEnd={this.handleRoundEnd}
              addRecording={this.addRecording}
              inactivityManager={this.inactivityManager}
              recordingEnd={this.recordingEnd}
              roundID={round}
              stopAllSounds={this.props.currentRound.stopAllSounds}
              currentRound={this.props.currentRound}
              playPressTheRedButton={playPressTheRedButton}
              recordings={this.recordings}
              commonSounds={this.props.commonSounds}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isVideoModalOpen: state.activity.isVideoModalOpen,
  commonSounds: state.ui.sounds.common
});

const mapDispatchToProps = {
  showVisualCue,
  hideVisualCue,
  sendRecorderSkippedEventData
};

VoiceRecorder.propTypes = {
  currentRound: PropTypes.object,
  micPermissions: PropTypes.bool,
  onRoundEnd: PropTypes.func,
  saveRecordings: PropTypes.func,
  isVideoModalOpen: PropTypes.bool,
  sendRecorderSkippedEventData: PropTypes.func,
  commonSounds: PropTypes.object,
  showVisualCue: PropTypes.func,
  hideVisualCue: PropTypes.func
};

export default connect(mapStateToProps, mapDispatchToProps)(VoiceRecorder);
